import { GoogleLogin } from "@react-oauth/google";
import { useGoogleAuthCallback, useSignIn } from "../../queries/auth";
import { decodeAuthToken } from "../../utils/auth";
import { useAuthStore } from "../../store/useAuthStore";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import SessionStorageService, {
  SessionStorageKeys,
} from "../../utils/sessionStorage";
import { useGoogleAuthStore } from "../../store/useGoogleAuthStore";
import { PlatformEnum, PlatformUrlMapping } from "../../shared/utils";
import { trackEvent } from "../../utils/AmpAnalytics";
import { WarningModal } from "./LoginComponents";
import { AuthStrategyType, UserType } from "../../types/auth";
import { useFeatureFlag } from "../../FeatureFlags";

export const GoogleLoginButton = () => {
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const isMarketplace = useFeatureFlag("allowUserRegistration");

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  let platform: PlatformEnum | null | undefined = queryParams.get(
    "platform"
  ) as PlatformEnum;
  let redirect = platform ? PlatformUrlMapping[platform] : undefined;
  const [credentialResponse, setCredentialResponse] = useState<string>("");
  const [loading, setLoading] = useState(false);

  // Only call the GoogleAuth callback when the credentialResponse and enabled change
  const { data, isError, error } = useGoogleAuthCallback(
    credentialResponse,
    enabled
  );

  const {
    email: googleEmail,
    isLoading,
    clear: clearGoogleAuth,
    data: googleData,
    accessToken: googleToken,
  } = useGoogleAuthStore();

  function ResetEmail() {
    sessionStorage.clear();
    useGoogleAuthStore.setState((store) => ({
      ...store,
      email: undefined,
      isLoading: false,
    }));
  }
  const sessionService = new SessionStorageService();
  const { mutate: signIn } = useSignIn();
  function HandleSelectedRole() {
    const userInfo = sessionService.getItem(
      SessionStorageKeys.ALLOWED_ACCESS
    ) as any[];
    if (googleToken && googleToken !== "") {
      setLoading(true);
      signIn(
        {
          email: googleEmail || "",
          authStrategyType: AuthStrategyType.GOOGLE,
          userType:
            userInfo[0].type === "EQUION" ? UserType.EQUION : UserType.EMPLOYEE,
          googleIdToken: googleToken || "",
        },
        {
          onSuccess: (data) => {
            setLoading(false);
            setShowWarning(false);
            if (data.data) {
              const accessToken = data.data;
              const decodedJwt = decodeAuthToken(accessToken);
              trackEvent("Logging In with Google", {
                email: googleEmail,
                name: decodedJwt.fullName,
              });
              const userType =
                userInfo[0].type === "EQUION" ? "Admin" : "Employee";
              toast("Signin Successful Signed in as " + userType, {
                type: "success",
                autoClose: 2000,
              });
              
              if (isMarketplace) {
                const marketplaceLink = process.env.REACT_APP_MARKETPLACE_URL;
                window.location.href = marketplaceLink || "/login";
              } else if (redirect) {
                window.location.href = redirect;
              } else if (
                googleData.data !== undefined &&
                googleData?.data?.length > 0 &&
                redirect === undefined
              ) {
                const allowedType = googleData.data?.find(
                  (data: any) => data.type === userInfo[0].type
                );
                if (allowedType && allowedType?.lastUsedProduct) {
                  const lastUsedProductLink =
                    PlatformUrlMapping[
                      allowedType?.lastUsedProduct as PlatformEnum
                    ];
                  window.location.href =
                    lastUsedProductLink || "/landing/dashboard";
                } else {
                  setAuth({
                    accessToken,
                    isAuthenticated: true,
                    email: decodedJwt.emailId,
                    name: decodedJwt.fullName,
                    id: decodedJwt.id,
                  });
                  navigate("/landing/dashboard");
                }
              } else {
                setAuth({
                  accessToken,
                  isAuthenticated: true,
                  email: decodedJwt.emailId,
                  name: decodedJwt.fullName,
                  id: decodedJwt.id,
                });
                navigate("/landing/dashboard");
              }

              sessionService.removeItem(SessionStorageKeys.GOOGLE_TOKEN);
              sessionService.removeItem(SessionStorageKeys.ALLOWED_ACCESS);
              sessionService.removeItem(SessionStorageKeys.LOGIN_STEP);
              clearGoogleAuth();
            }
          },
          onError: (err: any) => {
            setLoading(false);
            setShowWarning(false);
            const msg =
              err.response.data.message ??
              "Something went wrong, try again later";
            toast(msg, {
              type: "error",
              autoClose: 2000,
            });
          },
        }
      );
    }
  }

  function HandleSubmit() {
    if (data?.data?.data && data.data?.type === "token") {
      const accessToken = data.data.data;
      const decodedJwt = decodeAuthToken(accessToken);

      trackEvent("Logging In with Google", {
        email: decodedJwt.emailId,
        name: decodedJwt.fullName,
      });
      setEnabled(false);

      if (isMarketplace) {
        const marketplaceLink = process.env.REACT_APP_MARKETPLACE_URL;
        window.location.href = marketplaceLink || "/login";
      } else if (redirect) {
        window.location.href = redirect;
      } else if (data?.data?.userInfo[0]?.lastUsedProduct !== undefined) {
        const lastUsedProductLink =
          PlatformUrlMapping[
            data?.data?.userInfo[0]?.lastUsedProduct as PlatformEnum
          ];
        window.location.href = lastUsedProductLink || "/landing/dashboard";
      } else {
        setAuth({
          accessToken,
          isAuthenticated: true,
          email: decodedJwt.emailId,
          name: decodedJwt.fullName,
          id: decodedJwt.id,
        });
        navigate("/landing/dashboard");
      }
    }
    if (data?.data?.data && data.data?.type === "info") {
      useGoogleAuthStore.setState((store) => ({
        ...store,
        accessToken: credentialResponse,
        email: data.data?.data[0]?.email,
        data: data.data,
      }));
      if (data.data.data?.length === 1) {
        if (data?.data?.data[0]?.type === UserType.EMPLOYEE) {
          window.location.href =
            process.env.REACT_APP_EMPLOYEE_URL ||
            "https://employee.dev.hissa.com";
        } else if (
          data.data?.data[0].hasActiveSession === true &&
          data.data?.data[0].allowMultiAccess === false
        ) {
          setShowWarning(true);
        }
      } else sessionService.setItem(SessionStorageKeys.LOGIN_STEP, 2);
      sessionService.setItem(SessionStorageKeys.ALLOWED_ACCESS, data.data.data);
    }
  }

  useEffect(() => {
    if (data) HandleSubmit();
  }, [data]);

  useEffect(() => {
    if (isError) {
      toast((error as any).response?.data?.message, {
        type: "error",
        autoClose: 2000,
      });
      useGoogleAuthStore.setState((store) => ({ ...store, isLoading: false }));
      setEnabled(false);
    }
  }, [isError, error]);

  return (
    <>
      <div>
        <GoogleLogin
          onSuccess={(credentialResponse) => {
            setCredentialResponse(credentialResponse.credential!);
            useGoogleAuthStore.setState((store) => ({
              ...store,
              isLoading: true,
            }));
            setEnabled(true);
          }}
          onError={() => {
            console.log("Login Failed");
            setEnabled(false);
          }}
          useOneTap // Shows the "Sign in with Google" button automatically
          shape="rectangular"
          logo_alignment="center"
          theme="outline"
          width="275px"
        />
        {showWarning && (
          <WarningModal
            open={showWarning}
            onClose={() => {
              setShowWarning(false);
              ResetEmail();
            }}
            onSubmit={() => {
              setShowWarning(false);
              HandleSelectedRole();
            }}
            isLoading={loading}
          />
        )}
      </div>
    </>
  );
};
